import { useEffect, useState } from 'react';
import { Button } from '@vd/ui';
import { Check, Copy } from 'lucide-react';
import type { AgentCheck, Project } from '@vd/types';
import { useCopyFeedback } from '../../lib/useCopyFeedback';
import { formatWhen } from '../../lib/time';
import { AGENT_CHECK_POLL_MS, AGENT_CHECK_WAIT_MS, getAgentCheck, verificationPrompt } from './agentCheckApi';
import styles from './AgentSetup.module.css';

/**
 * "Check it works" on MCP setup (TOADO-974): the person asks their agent to
 * read one project, and the page says what Toado saw. Three kinds of answer,
 * never mixed up: seen by Toado (the agent read the project through its own
 * connection), said by the person (their word, labelled as such), and not
 * known. A copied command, a consent screen or a downloaded skill proves
 * nothing, so none of them counts.
 *
 * Every check belongs to one project (review round 1): choosing another, or
 * the workspace's projects changing under it, ends it, and nothing it found is
 * shown for another. A check also ends when its page goes, when its time is up
 * whether or not an answer came, and after a few failed asks in a row.
 */
type Phase =
  | { kind: 'idle' }
  | { kind: 'checking'; projectId: string }
  | { kind: 'answered'; projectId: string; check: Exclude<AgentCheck, { state: 'not_seen' }> }
  | { kind: 'waited'; projectId: string; connections: number | null }
  | { kind: 'failed'; projectId: string };

/** Asks that may fail in a row (a rate limit, a passing Redis blip) before the check gives up. */
const FAILURES_ALLOWED = 3;
/** One ask's own limit, so a request that never answers does not hold the check. */
const ASK_MS = 10_000;

// In the person's own zone (lib/time).
const at = (iso: string) => formatWhen(iso);

export function AgentConnectionCheck({ projects, pollMs = AGENT_CHECK_POLL_MS, waitMs = AGENT_CHECK_WAIT_MS }: {
  projects: readonly Project[];
  /** How often it asks and how long it waits: the defaults, but for tests. */
  pollMs?: number;
  waitMs?: number;
}) {
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '');
  const project = projects.find((candidate) => candidate.id === projectId) ?? projects[0] ?? null;
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [confirmed, setConfirmed] = useState<string | null>(null);
  const [copied, copy] = useCopyFeedback();
  // Which check is running: bumped by Check, Stop and a change of project.
  const [run, setRun] = useState(0);

  // A change of project, by choice or because the workspace changed, ends any
  // check: its polling too, not only what is shown (review round 2).
  useEffect(() => {
    setPhase({ kind: 'idle' });
    setConfirmed(null);
    setRun((current) => current + 1);
  }, [project?.id]);

  useEffect(() => {
    if (phase.kind !== 'checking' || !project || phase.projectId !== project.id) return;
    const checking = project.id;
    let over = false;
    let since: string | null = null;
    let failures = 0;
    let connections: number | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let asking: AbortController | null = null;
    const end = (next: Phase) => {
      if (over) return;
      over = true;
      if (timer) clearTimeout(timer);
      asking?.abort();
      setPhase(next);
    };
    // The check's own deadline, whether or not any answer comes.
    const deadline = setTimeout(() => end({ kind: 'waited', projectId: checking, connections }), waitMs);
    const ask = async () => {
      asking = new AbortController();
      const limit = setTimeout(() => asking?.abort(), ASK_MS);
      try {
        const answer = await getAgentCheck(checking, since ?? undefined, asking.signal);
        if (over) return;
        failures = 0;
        if (since === null) {
          // The first answer only sets the start: a read from before the check began does not count.
          since = answer.now;
        } else if (answer.check.state !== 'not_seen') {
          end({ kind: 'answered', projectId: checking, check: answer.check });
          return;
        } else {
          connections = answer.check.connections;
        }
      } catch {
        if (over) return;
        failures += 1;
        if (failures >= FAILURES_ALLOWED) {
          end({ kind: 'failed', projectId: checking });
          return;
        }
      } finally {
        clearTimeout(limit);
      }
      if (!over) timer = setTimeout(() => { void ask(); }, pollMs * (failures + 1));
    };
    void ask();
    return () => {
      // The page went, or the check was stopped or changed: nothing more is asked or shown.
      over = true;
      clearTimeout(deadline);
      if (timer) clearTimeout(timer);
      asking?.abort();
    };
    // One run per Check press, for one project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  const start = () => {
    if (!project) return;
    setConfirmed(null);
    setPhase({ kind: 'checking', projectId: project.id });
    setRun((current) => current + 1);
  };
  const stop = () => { setPhase({ kind: 'idle' }); setRun((current) => current + 1); };

  if (!project) {
    return <p className={styles.muted}>This workspace has no project for your agent to read yet. Make one, then come back to check.</p>;
  }
  const prompt = verificationPrompt(project);
  // What this project's check found; another project's never shows here.
  const shown = phase.kind !== 'idle' && phase.projectId === project.id ? phase : { kind: 'idle' as const };

  return (
    <div className={styles.check} data-testid="agent-check">
      {projects.length > 1 && (
        <label className={styles.field}>
          <span>The project your agent should work on</span>
          <select value={project.id} onChange={(event) => setProjectId(event.target.value)}>
            {projects.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}
          </select>
        </label>
      )}
      <p className={styles.muted}>
        Press Check, then paste this into your agent. It asks for a read of {project.name} and nothing else: no ticket is
        created, changed or started.
      </p>
      <pre className={styles.prompt} aria-label="The prompt for your agent">{prompt}</pre>
      <div className={styles.row}>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={copied === 'yes' ? <Check size={14} /> : <Copy size={14} />}
          onClick={() => { void copy(prompt); }}
        >
          {copied === 'yes' ? 'Copied' : 'Copy prompt'}
        </Button>
        {/* Said only when the clipboard took it (review round 5). */}
        {copied === 'failed' && <span className={styles.problem} role="status">Could not copy: select the prompt above and copy it yourself.</span>}
        {shown.kind === 'checking'
          ? <Button size="sm" variant="ghost" onClick={stop}>Stop checking</Button>
          : <Button size="sm" variant="primary" onClick={start}>{shown.kind === 'idle' ? 'Check' : 'Check again'}</Button>}
      </div>
      <Outcome phase={shown} projectName={project.name} />
      {(shown.kind === 'waited' || shown.kind === 'failed') && confirmed !== project.id && (
        <Button size="sm" variant="ghost" onClick={() => setConfirmed(project.id)}>My agent read it</Button>
      )}
      {confirmed === project.id && (
        <p className={styles.said} data-testid="agent-check-confirmed">
          You said your agent read {project.name}. Toado did not see it, so this is your word, not a check.
        </p>
      )}
    </div>
  );
}

function Outcome({ phase, projectName }: { phase: Phase; projectName: string }) {
  if (phase.kind === 'idle') return null;
  if (phase.kind === 'checking') {
    return <p className={styles.waiting} role="status" data-testid="agent-check-state">Watching for your agent to read {projectName}…</p>;
  }
  if (phase.kind === 'failed') {
    return <p className={styles.problem} role="status" data-testid="agent-check-state">The check could not be read just now. Try again in a moment.</p>;
  }
  if (phase.kind === 'waited') {
    return (
      <p className={styles.problem} role="status" data-testid="agent-check-state">
        {phase.connections === 0
          ? <>Not seen. You have not approved an MCP connection yet: finish your client&apos;s sign-in and approve it in the browser tab it opens. If you turned the approval down, start the sign-in again.</>
          : <>Not seen. {phase.connections === null ? 'Toado' : `You have approved ${phase.connections === 1 ? 'an MCP connection' : `${phase.connections} MCP connections`}, but Toado`} has not seen a read of {projectName} since you pressed Check. An approval alone does not show the client is set up: make sure it lists Toado&apos;s tools (restart it after adding the server), then paste the prompt again. If your agent says it read the board and Toado saw nothing, it may be signed in as another Toado account.</>}
      </p>
    );
  }
  const { check } = phase;
  if (check.state === 'seen') {
    return (
      <p className={styles.seen} role="status" data-testid="agent-check-state">
        <strong>Seen by Toado.</strong> {check.agent} read {projectName} at {at(check.at)}. Your agent is connected to the right project.
      </p>
    );
  }
  if (check.state === 'refused') {
    return (
      <p className={styles.problem} role="status" data-testid="agent-check-state">
        {check.agent} reached Toado at {at(check.at)}, but its connection leaves {projectName} out. Approve it again with
        this project included: run the client&apos;s sign-in again, or make a token that includes it.
      </p>
    );
  }
  return (
    <p className={styles.problem} role="status" data-testid="agent-check-state">
      {check.agent} read {check.project ? check.project.name : 'a project you can no longer see'} at {at(check.at)}, not {projectName}.
      Paste the prompt again: it names {projectName}. If your agent keeps reading another project, it may be signed in to another workspace.
    </p>
  );
}
